import type { IconName } from '@/components/ui';
import { can } from '@/features/auth/permissions';
import { AccessLevel, PermissionSubject, UserRole, type SessionUser } from '@/types/domain';

export interface NavItem {
  /**
   * Absent on a group, which is a heading that opens rather than a
   * destination. A group is identified by its label instead.
   */
  to?: string;
  label: string;
  icon: IconName;
  end?: boolean;
  /**
   * Which audience this is for. Omit to show it to every signed-in role.
   *
   * Still role-based because it answers a different question from `requires`:
   * whether an entry belongs to the platform operator's world or a tenant's.
   * A tenant's own people are separated by permission, not by role.
   */
  roles?: UserRole[];
  /**
   * The permission this entry needs, if any.
   *
   * An administrator holds everything, so this only ever hides things from
   * colleagues - which is the point: a firm that has not given somebody
   * payroll should not show them a Payroll menu that refuses them.
   */
  requires?: { subject: PermissionSubject; level?: AccessLevel };
  /** One level of nesting. Settings would otherwise be a list of ten. */
  children?: NavItem[];
}

export interface NavSection {
  title?: string;
  /**
   * Shown on the navbar's menu trigger, where the section title is the only
   * thing on screen until the menu opens. The sidebar prints the title as a
   * heading and has no use for it.
   */
  icon?: IconName;
  items: NavItem[];
}

/**
 * The navigation, as data. Shared by the sidebar, the navbar and the mobile
 * drawer so the three layouts can never drift apart.
 *
 * Filtering by role here is presentation only - the API enforces the same
 * boundary, so a hidden link is a convenience, not the access control.
 *
 * Settings nests one level deep. It accumulated ten entries as the HR and
 * accounting masters arrived, and a flat list that long stops being scannable:
 * grouping them by what they configure means somebody looking for "leave
 * types" reads four headings rather than ten links.
 */
const TENANT_ROLES = [UserRole.TENANT_ADMIN, UserRole.TENANT_CLIENT];

/** Shorthand for the two shapes every tenant entry takes. */
const reads = (subject: PermissionSubject) => ({
  roles: TENANT_ROLES,
  requires: { subject },
});

const edits = (subject: PermissionSubject) => ({
  roles: TENANT_ROLES,
  requires: { subject, level: AccessLevel.WRITE },
});

export const NAV_SECTIONS: NavSection[] = [
  {
    items: [
      /* `/` is the one shared path - it means different things per role, see
         ByRole in the router - so both audiences list it here. */
      { to: '/', label: 'Overview', icon: 'home', end: true },
      { to: '/tenants', label: 'Tenants', icon: 'building', roles: [UserRole.SUPER_ADMIN] },
      { to: '/users', label: 'Users', icon: 'users', roles: [UserRole.SUPER_ADMIN] },
    ],
  },
  {
    title: 'People',
    icon: 'users',
    items: [
      {
        to: '/employees',
        label: 'Employees',
        icon: 'users',
        ...reads(PermissionSubject.EMPLOYEES),
      },
      { to: '/leave', label: 'Leave', icon: 'activity', ...reads(PermissionSubject.LEAVE) },
      { to: '/payroll', label: 'Payroll', icon: 'layers', ...reads(PermissionSubject.PAYROLL) },
    ],
  },
  {
    title: 'Customers',
    icon: 'user',
    items: [
      {
        to: '/customers/individuals',
        label: 'Individuals',
        icon: 'user',
        ...reads(PermissionSubject.CUSTOMERS),
      },
      {
        to: '/customers/corporates',
        label: 'Corporates',
        icon: 'building',
        ...reads(PermissionSubject.CUSTOMERS),
      },
      /* Beside the customers rather than in a section of its own: a lead is
         the same list of people one step earlier, and the whole point of the
         feature is the step from one to the other. Its own permission, which
         ships closed - so for most firms this link is simply not there until
         they decide somebody should see the pipeline. */
      { to: '/leads', label: 'Leads', icon: 'activity', ...reads(PermissionSubject.LEADS) },
      {
        to: '/quotations',
        label: 'Quotations',
        icon: 'document',
        ...reads(PermissionSubject.BILLING),
      },
      {
        to: '/invoices',
        label: 'Invoices',
        icon: 'document',
        ...reads(PermissionSubject.BILLING),
      },
    ],
  },
  {
    title: 'Work',
    icon: 'folder',
    items: [
      { to: '/jobs', label: 'Jobs', icon: 'activity', ...reads(PermissionSubject.JOBS) },
      {
        to: '/documents',
        label: 'Documents',
        icon: 'documents',
        ...reads(PermissionSubject.DOCUMENTS),
      },
    ],
  },
  {
    title: 'Finance',
    icon: 'building',
    items: [
      {
        to: '/accounts',
        label: 'Overview',
        icon: 'building',
        end: true,
        ...reads(PermissionSubject.ACCOUNTS),
      },
      {
        to: '/accounts/transactions',
        label: 'Transactions',
        icon: 'documents',
        ...reads(PermissionSubject.ACCOUNTS),
      },
    ],
  },
  {
    title: 'Settings',
    icon: 'settings',
    items: [
      {
        label: 'Organisation',
        icon: 'settings',
        roles: TENANT_ROLES,
        children: [
          {
            to: '/settings/services',
            label: 'Services',
            icon: 'layers',
            ...edits(PermissionSubject.SETTINGS),
          },
          {
            to: '/settings/flows',
            label: 'Work flows',
            icon: 'activity',
            ...edits(PermissionSubject.SETTINGS),
          },
          {
            to: '/access',
            label: 'Sign-in access',
            icon: 'key',
            ...edits(PermissionSubject.ACCESS),
          },
          /* Beside it, because the two halves of one question: who may sign
             in, and what they may reach once they have. */
          {
            to: '/settings/access-profiles',
            label: 'Access profiles',
            icon: 'shield',
            ...edits(PermissionSubject.ACCESS),
          },
          /* Who did what, inside this organisation. It sits beside sign-in
             access because it answers the question that one raises. */
          {
            to: '/settings/audit',
            label: 'Audit log',
            icon: 'activity',
            ...reads(PermissionSubject.AUDIT),
          },
        ],
      },
      {
        label: 'People & pay',
        icon: 'users',
        ...edits(PermissionSubject.SETTINGS),
        children: [
          { to: '/settings/leave-types', label: 'Leave types', icon: 'layers' },
          { to: '/settings/salary-components', label: 'Salary components', icon: 'layers' },
          { to: '/settings/holidays', label: 'Public holidays', icon: 'activity' },
          { to: '/settings/hr', label: 'HR settings', icon: 'settings' },
        ],
      },
      {
        label: 'Finance',
        icon: 'building',
        ...edits(PermissionSubject.SETTINGS),
        children: [
          { to: '/settings/billing', label: 'Billing settings', icon: 'settings' },
          { to: '/settings/categories', label: 'Income & expense', icon: 'layers' },
          { to: '/settings/financial-accounts', label: 'Cash & bank', icon: 'building' },
        ],
      },
      {
        label: 'Records',
        icon: 'archive',
        ...edits(PermissionSubject.SETTINGS),
        children: [
          { to: '/settings/document-types', label: 'Document types', icon: 'archive' },
          { to: '/settings/document-labels', label: 'Document labels', icon: 'tag' },
          { to: '/settings/countries', label: 'Countries', icon: 'layers' },
          { to: '/settings/visa-statuses', label: 'Visa statuses', icon: 'key' },
          { to: '/settings/relationship-types', label: 'Relationship types', icon: 'users' },
          { to: '/settings/lead-stages', label: 'Lead stages', icon: 'activity' },
          { to: '/settings/lead-sources', label: 'Lead sources', icon: 'tag' },
        ],
      },
    ],
  },
  {
    title: 'Masters',
    icon: 'layers',
    items: [
      {
        to: '/masters/industries',
        label: 'Industries',
        icon: 'layers',
        roles: [UserRole.SUPER_ADMIN],
      },
    ],
  },
  {
    title: 'System',
    icon: 'shield',
    items: [{ to: '/audit', label: 'Audit log', icon: 'activity', roles: [UserRole.SUPER_ADMIN] }],
  },
];

/** Whether this person may see the item at all: the right audience, and the
    permission it needs. */
function allowed(item: NavItem, user: SessionUser | null | undefined): boolean {
  if (item.roles && !(user && item.roles.includes(user.role))) {
    return false;
  }

  return !item.requires || can(user?.permissions, item.requires.subject, item.requires.level);
}

/**
 * Drops sections, groups and items the signed-in user has no access to.
 *
 * A group whose children are all hidden is dropped with them - an "Organisation"
 * heading that opens onto nothing is worse than no heading.
 */
export function visibleSections(user: SessionUser | null | undefined): NavSection[] {
  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.flatMap((item) => {
      if (!allowed(item, user)) {
        return [];
      }

      if (!item.children) {
        return [item];
      }

      const children = item.children.filter((child) => allowed(child, user));

      return children.length > 0 ? [{ ...item, children }] : [];
    }),
  })).filter((section) => section.items.length > 0);
}

/** A nav entry that actually goes somewhere. */
export type NavLeaf = NavItem & { to: string };

/** A heading inside a navbar menu. Untitled when the items sit straight under it. */
export interface NavMenuGroup {
  title?: string;
  items: NavLeaf[];
}

/**
 * One entry on the horizontal navbar: either a destination or a menu that
 * holds several.
 */
export type NavEntry =
  | { kind: 'link'; item: NavLeaf }
  | { kind: 'menu'; label: string; icon: IconName; groups: NavMenuGroup[] };

/**
 * The navbar's model, folded down from the same sections the sidebar reads.
 *
 * A horizontal bar has one line to spend, and the flat list had grown to
 * twenty links - most of them settings - which is past the point where a row
 * of tabs can be scanned. So a titled section becomes one menu, and the
 * groups inside it become headings within that menu: the bar drops to a
 * handful of entries and the two-level structure survives.
 *
 * Two shapes stay flat. The untitled first section has no label to collapse
 * under, and a section holding a single destination is that destination - a
 * menu that opens onto one item asks for a click and gives nothing back.
 */
export function navEntries(user: SessionUser | null | undefined): NavEntry[] {
  return visibleSections(user).flatMap((section): NavEntry[] => {
    const direct = section.items.filter(
      (item): item is NavLeaf => item.children === undefined && item.to !== undefined,
    );
    const nested = section.items.filter((item) => item.children !== undefined);

    if (section.title === undefined) {
      return [
        ...direct.map((item): NavEntry => ({ kind: 'link', item })),
        ...nested.map((item): NavEntry => ({
          kind: 'menu',
          label: item.label,
          icon: item.icon,
          groups: [{ items: leavesOf(item.children) }],
        })),
      ];
    }

    if (nested.length === 0 && direct.length === 1 && direct[0]) {
      return [{ kind: 'link', item: direct[0] }];
    }

    const groups: NavMenuGroup[] = [
      ...(direct.length > 0 ? [{ items: direct }] : []),
      ...nested.map((item) => ({ title: item.label, items: leavesOf(item.children) })),
    ];

    return [
      {
        kind: 'menu',
        label: section.title,
        icon: section.icon ?? section.items[0]?.icon ?? 'layers',
        groups,
      },
    ];
  });
}

function leavesOf(items: NavItem[] | undefined): NavLeaf[] {
  return (items ?? []).filter((item): item is NavLeaf => item.to !== undefined);
}

/**
 * Whether `pathname` is inside `item` - the test NavLink applies to itself,
 * repeated here for the menu trigger, which has to light up for a child it is
 * currently hiding.
 */
export function navMatches(item: NavLeaf, pathname: string): boolean {
  if (item.end) {
    return pathname === item.to;
  }

  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

/** Whether any destination in the menu is the current page. */
export function menuHoldsPath(groups: NavMenuGroup[], pathname: string): boolean {
  return groups.some((group) => group.items.some((item) => navMatches(item, pathname)));
}
